import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, List, MoreHorizontal, X } from 'lucide-react';
import ChapterReaderControlsSheet from './ChapterReaderControlsSheet';
import { generateChapterLink } from '@/utils/utils';
import { getCapChapterId, getCapNome, getCapNum } from '@/utils/capitulo-fields';

interface ChapterBottomNavbarProps {
    capitulo: any;
    chapters: any[];
    /** id do capítulo na rota (fallback para marcar o atual na lista) */
    routeChapterId?: string;
    imageZoom: number;
    onImageZoomChange: (value: number) => void;
    imageBrightness: number;
    onImageBrightnessChange: (value: number) => void;
    infiniteScroll: boolean;
    onToggleInfiniteScroll: () => void;
    onScrollToTop: () => void;
    controlsVisible: boolean;
    doubleTapControlsOnly: boolean;
    onToggleDoubleTapControlsOnly: () => void;
    onDownloadChapter: () => void;
    isDownloadingChapter: boolean;
    isFullscreen?: boolean;
    onToggleFullscreen?: () => void;
    showFullscreen?: boolean;
    /** Leitor horizontal (formato Manga): navegação por página na barra. */
    mangaReader?: {
        currentPage: number;
        totalPages: number;
        onPrevPage: () => void;
        onNextPage: () => void;
    } | null;
    /** Capítulo “atual” para prev/next/lista (ex.: scroll infinito manga). Se omitido, usa a rota. */
    navigationChapterId?: string | null;
}

function sortKey(c: any): number {
    return parseFloat(String(c?.cap_num ?? c?.numero ?? 0)) || 0;
}

function sortedByNumber(list: any[]) {
    return [...list].sort((a, b) => sortKey(a) - sortKey(b));
}

function capEntryId(c: any): string {
    return String(c?.cap_id ?? c?.id ?? '');
}

export default function ChapterBottomNavbar({
    capitulo,
    chapters,
    routeChapterId,
    imageZoom,
    onImageZoomChange,
    imageBrightness,
    onImageBrightnessChange,
    infiniteScroll,
    onToggleInfiniteScroll,
    onScrollToTop,
    controlsVisible,
    doubleTapControlsOnly,
    onToggleDoubleTapControlsOnly,
    onDownloadChapter,
    isDownloadingChapter,
    isFullscreen = false,
    onToggleFullscreen,
    showFullscreen = false,
    mangaReader = null,
    navigationChapterId = null,
}: ChapterBottomNavbarProps) {
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);

    const currentKey =
        navigationChapterId != null && navigationChapterId !== ''
            ? String(navigationChapterId)
            : routeChapterId ?? String(getCapChapterId(capitulo) ?? '');
    const currentNumLabel = String(getCapNum(capitulo) ?? getCapNome(capitulo) ?? '');

    const ordered = useMemo(() => sortedByNumber(chapters ?? []), [chapters]);

    const currentIndex = useMemo(
        () => ordered.findIndex((c) => capEntryId(c) === currentKey),
        [ordered, currentKey]
    );

    const prevChapter = currentIndex > 0 ? ordered[currentIndex - 1] : null;
    const nextChapter = currentIndex >= 0 && currentIndex < ordered.length - 1 ? ordered[currentIndex + 1] : null;

    const goTo = (c: any) => {
        const id = c?.cap_id ?? c?.id;
        if (id == null) return;
        navigate(generateChapterLink(id));
        setModalOpen(false);
        setMoreOpen(false);
    };

    useEffect(() => {
        if (!modalOpen && !moreOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [modalOpen, moreOpen]);

    return (
        <>
            {/* Mesmo estilo de ChapterMobileControls: pílula flutuante, desktop + mobile */}
            <nav
                className={`capitulo-mobile-actions fixed z-40 left-1/2 -translate-x-1/2 bottom-[max(1.5rem,env(safe-area-inset-bottom))] bg-black/80 backdrop-blur-xl border border-white/10 rounded-full w-[92%] max-w-xl md:max-w-none md:w-auto px-3 md:px-5 py-3 flex items-center justify-between md:justify-center md:gap-3 shadow-2xl transition-all duration-300 ${
                    controlsVisible ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-16 opacity-0 pointer-events-none'
                }`}
                aria-label="Navegação do capítulo"
            >
                <button
                    type="button"
                    disabled={!prevChapter}
                    onClick={() => prevChapter && goTo(prevChapter)}
                    className={`p-2 rounded-full transition-colors shrink-0 ${
                        prevChapter ? 'hover:bg-white/10 text-white' : 'text-white/20 cursor-not-allowed'
                    }`}
                    title="Capítulo anterior"
                >
                    <ChevronLeft size={22} />
                </button>

                <div className="relative md:flex-initial flex justify-center min-w-0">
                    <button
                        type="button"
                        onClick={() => {
                            setMoreOpen(false);
                            setModalOpen(true);
                        }}
                        className="flex items-center gap-2 px-2 md:px-3 py-1.5 hover:bg-white/10 rounded-lg transition-colors font-mono text-sm text-white max-w-full"
                        title="Lista de capítulos"
                    >
                        <List size={18} className="shrink-0 text-white/90" />
                        <span className="truncate">
                            {mangaReader ? (
                                <>
                                    Cap. {currentNumLabel}
                                    <span className="text-white/50 font-normal ml-1.5">
                                        · {mangaReader.currentPage}/{mangaReader.totalPages}
                                    </span>
                                </>
                            ) : (
                                <>Cap. {currentNumLabel}</>
                            )}
                        </span>
                    </button>
                </div>

                <button
                    type="button"
                    onClick={() => {
                        setModalOpen(false);
                        setMoreOpen((o) => !o);
                    }}
                    className={`p-2 rounded-full transition-colors shrink-0 ${moreOpen ? 'bg-orange-500/25 text-orange-400' : 'hover:bg-white/10 text-white/80'}`}
                    title="Zoom, brilho, leitura infinita…"
                >
                    <MoreHorizontal size={22} />
                </button>

                <button
                    type="button"
                    disabled={!nextChapter}
                    onClick={() => nextChapter && goTo(nextChapter)}
                    className={`p-2 rounded-full transition-colors shrink-0 ${
                        nextChapter ? 'hover:bg-white/10 text-white' : 'text-white/20 cursor-not-allowed'
                    }`}
                    title="Próximo capítulo"
                >
                    <ChevronRight size={22} />
                </button>
            </nav>

            <ChapterReaderControlsSheet
                open={moreOpen}
                onClose={() => setMoreOpen(false)}
                imageZoom={imageZoom}
                onImageZoomChange={onImageZoomChange}
                imageBrightness={imageBrightness}
                onImageBrightnessChange={onImageBrightnessChange}
                infiniteScroll={infiniteScroll}
                onToggleInfiniteScroll={onToggleInfiniteScroll}
                onScrollToTop={onScrollToTop}
                doubleTapControlsOnly={doubleTapControlsOnly}
                onToggleDoubleTapControlsOnly={onToggleDoubleTapControlsOnly}
                onDownloadChapter={onDownloadChapter}
                isDownloadingChapter={isDownloadingChapter}
                isFullscreen={isFullscreen}
                onToggleFullscreen={onToggleFullscreen}
                showFullscreen={showFullscreen}
            />

            <AnimatePresence>
                {modalOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[200] bg-black/60"
                            onClick={() => setModalOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 z-[201] w-full md:left-1/2 md:right-auto md:max-w-2xl md:-translate-x-1/2 bg-neutral-900 border-t border-white/10 md:border-x md:border-white/10 rounded-t-2xl max-h-[85vh] flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-center pt-3 pb-2 shrink-0">
                                <div className="w-10 h-1 bg-white/20 rounded-full" />
                            </div>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 shrink-0">
                                <h2 id="chapter-list-title" className="text-lg font-semibold text-white">
                                    Capítulos
                                </h2>
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className="p-2 rounded-lg hover:bg-white/5 text-white/50 hover:text-white"
                                    aria-label="Fechar"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <div
                                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y px-4 py-3 relative"
                                style={{ WebkitOverflowScrolling: 'touch' }}
                            >
                                {ordered.length === 0 ? (
                                    <p className="text-center text-white/50 text-sm py-8">Nenhum capítulo listado.</p>
                                ) : (
                                    <div className="grid grid-cols-2 gap-2">
                                        {ordered.map((c) => {
                                            const id = capEntryId(c);
                                            const isActive = id === currentKey;
                                            const num = c.cap_num ?? c.numero;
                                            return (
                                                <button
                                                    key={id || `cap-${num}`}
                                                    type="button"
                                                    onClick={() => goTo(c)}
                                                    className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                                        isActive
                                                            ? 'bg-orange-500 text-white'
                                                            : 'bg-white/5 text-white/80 hover:bg-white/10 hover:text-white'
                                                    }`}
                                                >
                                                    Cap. {num}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
